import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, MapPin, Flag, Banknote, Smartphone, CheckCircle2, Package, User, Phone, Wallet, MessageCircle, Send, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import LiveRideMap from "../../components/app/LiveRideMap";
import AddressAutocomplete from "../../components/app/AddressAutocomplete";
import PlaceInput from "../../components/app/PlaceInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { RateDialog } from "../../components/app/RideWidgets";
import { ArrivedWaiting } from "../../components/app/ArrivedWaiting";
import { ridesApi, errorMessage, inr } from "../../lib/api";
import { paymentsApi, openCheckout } from "../../lib/razorpay";
import { useAuth } from "../../context/AuthContext";
import ChatUnreadBadge from "../../components/app/ChatUnreadBadge";
import GoodsProgress from "../../components/app/GoodsProgress";
import DriverSafetyCard from "../../components/app/DriverSafetyCard";

const ACTIVE = ["searching", "fee_offer", "driver_assigned", "driver_arriving", "in_progress", "payment_pending", "no_driver"];
const EDITABLE = ["driver_assigned", "driver_arriving", "in_progress"];

function ElapsedBadge({ since }) {
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((n) => n + 1), 1000); return () => clearInterval(t); }, []);
  const start = since ? new Date(since).getTime() : Date.now();
  const s = Math.max(0, Math.floor((Date.now() - start) / 1000));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return <span data-testid="goods-search-elapsed" className="font-mono">{mm}:{ss}</span>;
}

function GoodsInfo({ ride, showFare }) {
  return (
    <div className="space-y-2 text-sm" data-testid="trip-goods-info">
      {showFare && (
        <div className="mb-1 flex items-center justify-between">
          <div>
            <p className="text-xs text-ink-muted">Delivery fee</p>
            <p className="text-lg font-bold text-ink" data-testid="trip-goods-fare">{inr(ride.fare)} <span className="text-xs font-medium text-ink-muted">· {ride.distance_km} km</span></p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand"><Package className="h-3.5 w-3.5" /> Goods</span>
        </div>
      )}
      <p className="flex items-center gap-2 text-ink-soft"><MapPin className="h-3.5 w-3.5 text-emerald-600" /> <span className="truncate">{ride.pickup_address}</span></p>
      <p className="flex items-center gap-2 font-medium text-ink"><Flag className="h-3.5 w-3.5 text-brand" /> <span className="truncate">{ride.drop_address}</span></p>
      {(ride.receiver_name || ride.receiver_phone) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl bg-surface-100 px-3 py-2">
          {ride.receiver_name && <span className="flex items-center gap-1.5 text-ink-soft"><User className="h-3.5 w-3.5" /> {ride.receiver_name}</span>}
          {ride.receiver_phone && <a href={`tel:${ride.receiver_phone}`} className="flex items-center gap-1.5 text-ink-soft hover:text-brand"><Phone className="h-3.5 w-3.5" /> {ride.receiver_phone}</a>}
          {ride.weight_kg != null && <span className="text-ink-muted">{ride.weight_kg} kg</span>}
        </div>
      )}
      {ride.delivery_instructions && <p className="text-ink-muted">“{ride.delivery_instructions}”</p>}
      {ride.cod_payer && (
        <p className="flex items-center gap-1.5 text-xs font-semibold text-ink-soft"><Wallet className="h-3.5 w-3.5" /> {ride.cod_payer === "receiver" ? "Receiver pays the delivery fee on drop-off" : "You pay the delivery fee"}</p>
      )}
    </div>
  );
}

// Full-screen live trip map with in-ride edit controls (add stop / change drop).
export default function LiveTrip() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ride, setRide] = useState(undefined); // undefined = loading, null = none
  const [edit, setEdit] = useState(null); // null | "stop" | "drop"
  const editIntentRef = useRef(false);
  const [picked, setPicked] = useState(null);
  const [payFailed, setPayFailed] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [rateRide, setRateRide] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [goodsEdit, setGoodsEdit] = useState(false);
  const [gForm, setGForm] = useState({ receiver_name: "", receiver_phone: "", delivery_instructions: "", drop: null });
  const [gBusy, setGBusy] = useState(false);
  const [gQuote, setGQuote] = useState(null);
  const trackedRef = useRef(null);

  useEffect(() => {
    let on = true;
    const load = () =>
      ridesApi
        .list()
        .then((rows) => {
          if (!on) return;
          const active = (rows || []).find((r) => ACTIVE.includes(r.status)) || null;
          if (active) { trackedRef.current = active.ride_id; setRide(active); return; }
          const tid = trackedRef.current;
          if (tid) {
            const done = (rows || []).find((r) => r.ride_id === tid && r.status === "completed" && !r.rating);
            if (done) { setRateRide((prev) => prev || done); trackedRef.current = null; }
          }
          setRide(null);
        })
        .catch(() => { if (on) setRide(null); });
    load();
    const t = setInterval(load, 6000);
    return () => { on = false; clearInterval(t); };
  }, []);

  // Share the rider's own live location during an active taxi ride so a shared trip link
  // can show the rider's pin next to the driver's.
  const rideId = ride?.ride_id;
  const rideActive = ride && ACTIVE.includes(ride.status) && ride.purpose !== "goods";
  useEffect(() => {
    if (!rideId || !rideActive || !navigator.geolocation) return undefined;
    let on = true;
    const ping = () => navigator.geolocation.getCurrentPosition(
      (p) => { if (on) ridesApi.riderLocation(rideId, p.coords.latitude, p.coords.longitude).catch(() => {}); },
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 8000 },
    );
    ping();
    const t = setInterval(ping, 12000);
    return () => { on = false; clearInterval(t); };
  }, [rideId, rideActive]);

  // Open the Change-drop / Add-stop dialog directly when arriving with ?edit=drop|stop
  // (e.g. from the home "Change drop" shortcut), once the ride is editable.
  useEffect(() => {
    const intent = searchParams.get("edit");
    if (!intent || editIntentRef.current || !ride) return;
    const editable = EDITABLE.includes(ride.status) && ride.purpose !== "goods";
    if (!editable) return;
    editIntentRef.current = true;
    setEdit(intent === "stop" ? "stop" : "drop");
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
  }, [ride, searchParams, setSearchParams]);


  const shareReceiver = async () => {
    if (!ride) return;
    setSharing(true);
    try {
      const res = await ridesApi.notifyReceiver(ride.ride_id);
      toast.success(res.whatsapp ? "Tracking link sent to the receiver on WhatsApp" : "Tracking link ready (WhatsApp not configured)");
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSharing(false); }
  };

  const submitRate = async (body) => {
    if (!rateRide) return;
    try { await ridesApi.rate(rateRide.ride_id, body); toast.success("Thanks for rating!"); }
    catch (e) { toast.error(errorMessage(e)); throw e; }
  };

  const closeEdit = () => { setEdit(null); setPicked(null); setText(""); };

  const submitEdit = async () => {
    if (!picked || !ride) return toast.error("Pick a location on the list");
    setBusy(true);
    try {
      const body = { lat: picked.lat, lng: picked.lng, address: picked.address };
      const updated = edit === "stop"
        ? await ridesApi.addStop(ride.ride_id, body)
        : await ridesApi.changeDrop(ride.ride_id, body);
      setRide(updated);
      toast.success(`${edit === "stop" ? "Stop added" : "Drop updated"} — new fare ${inr(updated.fare)} (${updated.distance_km} km)`);
      closeEdit();
    } catch (e) { toast.error(errorMessage(e, "Could not update the trip")); }
    finally { setBusy(false); }
  };

  const reload = async () => {
    try { const rows = await ridesApi.list(); setRide((rows || []).find((r) => ACTIVE.includes(r.status)) || null); } catch { /* ignore */ }
  };
  const removeStop = async (index) => {
    if (!ride) return;
    setBusy(true);
    try {
      const updated = await ridesApi.removeStop(ride.ride_id, index);
      setRide(updated);
      toast.success(`Stop removed — new fare ${inr(updated.fare)} (${updated.distance_km} km)`);
    } catch (e) { toast.error(errorMessage(e, "Could not remove the stop")); }
    finally { setBusy(false); }
  };
  const chooseCash = async () => {
    try { const u = await ridesApi.choosePayment(ride.ride_id, "cash"); setRide(u); toast.success("Pay your driver in cash"); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const payOnline = async () => {
    try {
      // Safe retry: if an earlier attempt already went through, confirm it instead of re-charging.
      const existing = await paymentsApi.recheckRide(ride.ride_id);
      if (existing?.payment_status === "paid") { setRide(existing); toast.success("Payment already received"); return; }
      await ridesApi.choosePayment(ride.ride_id, "razorpay");
      const order = await paymentsApi.createRideOrder(ride.ride_id);
      const res = await openCheckout({ order, amount: ride.fare, user, description: "Ride fare" });
      const u = await paymentsApi.verifyRide({ ride_id: ride.ride_id, razorpay_order_id: res.razorpay_order_id || order.order_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature || null });
      setRide(u); toast.success("Payment successful");
    } catch (e) {
      if (!e?.cancelled) { setPayFailed(true); toast.error(errorMessage(e, "Payment couldn't be confirmed. Please retry — you won't be charged twice.")); }
      reload();
    }
  };
  const cancelGoods = async () => {
    if (!window.confirm("Cancel this delivery? You'll get a full refund.")) return;
    try { const u = await ridesApi.cancel(ride.ride_id); setRide(u); toast.success("Delivery cancelled — refund issued"); }
    catch (e) { toast.error(errorMessage(e, "Couldn't cancel")); }
  };

  const cancelGoodsAssigned = async () => {
    if (!window.confirm("Cancel this delivery? A cancellation fee may apply (prepaid: fee kept and the rest refunded; cash: fee added to your next ride).")) return;
    try { const u = await ridesApi.cancel(ride.ride_id); setRide(u); toast.success("Delivery cancelled"); }
    catch (e) { toast.error(errorMessage(e, "Couldn't cancel")); }
  };

  const openGoodsEdit = () => {
    setGForm({
      receiver_name: ride.receiver_name || "",
      receiver_phone: ride.receiver_phone || "",
      delivery_instructions: ride.delivery_instructions || "",
      drop: ride.drop_lat ? { lat: ride.drop_lat, lng: ride.drop_lng, name: ride.drop_address } : null,
    });
    setGQuote(null);
    setGoodsEdit(true);
  };

  const gDirty = goodsEdit && (
    gForm.receiver_name.trim() !== (ride.receiver_name || "").trim() ||
    gForm.receiver_phone.trim() !== (ride.receiver_phone || "").trim() ||
    gForm.delivery_instructions !== (ride.delivery_instructions || "") ||
    (gForm.drop && (gForm.drop.lat !== ride.drop_lat || gForm.drop.lng !== ride.drop_lng))
  );
  const gDropChanged = !!(gForm.drop && (gForm.drop.lat !== ride.drop_lat || gForm.drop.lng !== ride.drop_lng));
  const buildGoodsBody = (preview) => {
    const body = {
      receiver_name: gForm.receiver_name.trim(),
      receiver_phone: gForm.receiver_phone.trim(),
      delivery_instructions: gForm.delivery_instructions,
    };
    if (gDropChanged) { body.drop_lat = gForm.drop.lat; body.drop_lng = gForm.drop.lng; body.drop_address = gForm.drop.name; }
    if (preview) body.preview = true;
    return body;
  };
  const saveGoodsEdit = async () => {
    // Drop-off change re-quotes the fare — show a summary to confirm before committing.
    if (gDropChanged) {
      setGBusy(true);
      try {
        const q = await ridesApi.goodsEdit(ride.ride_id, buildGoodsBody(true));
        setGQuote(q);
      } catch (e) { toast.error(errorMessage(e, "Could not calculate the new fare")); }
      finally { setGBusy(false); }
      return;
    }
    await commitGoodsEdit();
  };
  const commitGoodsEdit = async () => {
    setGBusy(true);
    try {
      const u = await ridesApi.goodsEdit(ride.ride_id, buildGoodsBody(false));
      setRide(u);
      toast.success(u?.edit_message || "Delivery updated");
      setGQuote(null);
      setGoodsEdit(false);
    } catch (e) { toast.error(errorMessage(e, "Could not update delivery")); }
    finally { setGBusy(false); }
  };
  const retrySearch = async () => {
    try { const u = await ridesApi.retrySearch(ride.ride_id); setRide(u); toast.success("Looking for a delivery partner again…"); }
    catch (e) { toast.error(errorMessage(e, "Couldn't retry")); }
  };

  if (ride === undefined) {
    return (
      <div className="fixed inset-0 z-[999] flex items-center justify-center bg-ink/90 text-white" data-testid="live-trip-loading">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (!ride) {
    if (rateRide) {
      return (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-ink/90 p-6 text-white" data-testid="trip-rate-overlay">
          <RateDialog
            open
            title={rateRide.purpose === "goods" ? "Rate your delivery partner" : "Rate your trip"}
            onOpenChange={(o) => { if (!o) { setRateRide(null); navigate("/app/home"); } }}
            onRate={submitRate}
          />
        </div>
      );
    }
    return <Navigate to="/app/home" replace />;
  }

  const isGoods = ride.purpose === "goods";
  const canEdit = EDITABLE.includes(ride.status) && !isGoods;
  const partnerWord = isGoods ? "delivery partner" : "driver";
  const stops = ride.stops || [];

  return (
    <div className="fixed inset-0 z-[999] bg-ink" data-testid="live-trip-screen">
      <LiveRideMap ride={ride} className="h-full w-full" />
      <Link
        to="/app/home"
        className="absolute left-4 top-4 z-[1000] inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-2 text-sm font-semibold text-ink shadow-lg shadow-ink/10 backdrop-blur hover:bg-white"
        data-testid="live-trip-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>

      {ride.status === "driver_arriving" && ride.arrived_at ? (
        <div className="absolute inset-x-4 top-16 z-[1000] mx-auto flex max-w-md justify-center" data-testid="rider-arrived-banner">
          <ArrivedWaiting arrivedAt={ride.arrived_at} taxi={ride.purpose !== "goods"} graceReminder who={ride.purpose === "goods" ? "Your delivery partner" : "Your driver"} testid="rider-arrived-waiting" />
        </div>
      ) : !["completed", "cancelled"].includes(ride.status) && ride.driver_eta_at && (Date.now() - new Date(ride.driver_eta_at).getTime() < 15 * 60 * 1000) && (
        <div className="absolute inset-x-4 top-16 z-[1000] mx-auto max-w-md rounded-xl bg-green-600 px-4 py-3 text-center text-sm font-bold text-white shadow-lg" data-testid="rider-eta-banner">
          Your driver is {ride.driver_eta_min > 0 ? `arriving in ~${ride.driver_eta_min} min` : "arriving now"}
        </div>
      )}

      {canEdit && (
        <div className="absolute inset-x-0 bottom-0 z-[1000] rounded-t-2xl bg-white p-4 shadow-2xl" data-testid="trip-edit-panel">
          <div className="mx-auto max-w-2xl space-y-3">
            {ride.driver && ride.status !== "in_progress" && <DriverSafetyCard driver={ride.driver} ride={ride} />}
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-xs text-ink-muted">Fare so far</p>
                <p className="text-lg font-bold text-ink" data-testid="trip-edit-fare">{inr(ride.fare)} <span className="text-xs font-medium text-ink-muted">· {ride.distance_km} km</span></p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setEdit("stop"); }} data-testid="trip-add-stop"><Plus className="mr-1 h-4 w-4" /> Add stop</Button>
                <Button size="sm" className="rounded-full bg-brand text-white hover:bg-brand-600" onClick={() => { setEdit("drop"); }} data-testid="trip-change-drop"><Flag className="mr-1 h-4 w-4" /> Change drop</Button>
              </div>
            </div>
            <div className="space-y-1 text-sm">
              <p className="flex items-center gap-2 text-ink-soft"><MapPin className="h-3.5 w-3.5 text-emerald-600" /> <span className="truncate">{ride.pickup_address}</span></p>
              {stops.map((s, i) => (
                <p key={i} className="flex items-center gap-2 text-ink-soft" data-testid={`trip-stop-${i}`}>
                  <MapPin className="h-3.5 w-3.5 text-sunny-600" /> <span className="flex-1 truncate">Stop {i + 1}: {s.address}</span>
                  <button onClick={() => removeStop(i)} disabled={busy} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40" data-testid={`trip-remove-stop-${i}`}><X className="h-3.5 w-3.5" /> Remove</button>
                </p>
              ))}
              <p className="flex items-center gap-2 font-medium text-ink"><Flag className="h-3.5 w-3.5 text-brand" /> <span className="truncate">{ride.drop_address}</span></p>
            </div>
          </div>
        </div>
      )}

      {["searching", "fee_offer"].includes(ride.status) && (
        <div className="absolute inset-x-0 bottom-0 z-[1000] rounded-t-2xl bg-white p-4 shadow-2xl" data-testid="trip-searching-panel">
          <div className="mx-auto max-w-2xl space-y-3">
            <div className="flex items-center gap-3">
              <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-brand/10 text-brand">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand/20" />
                <Loader2 className="relative h-5 w-5 animate-spin" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-lg font-bold text-ink">Finding a {partnerWord}… {isGoods && <ElapsedBadge since={ride.search_cycle_started_at || ride.created_at} />}</p>
                <p className="text-sm text-ink-soft">{isGoods ? "No partner right now — we'll keep trying and assign one soon. You can cancel anytime for a full refund." : "We're matching you with a nearby driver."}</p>
              </div>
            </div>
            {isGoods && (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-200" data-testid="goods-search-progress">
                <div className="h-full w-2/3 animate-pulse rounded-full bg-brand" />
              </div>
            )}
            {isGoods && <GoodsProgress ride={ride} />}
            {isGoods && <GoodsInfo ride={ride} />}
            {isGoods && (
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 rounded-full" onClick={openGoodsEdit} data-testid="goods-edit-open"><Pencil className="mr-2 h-4 w-4" /> Edit</Button>
                <Button variant="outline" className="flex-1 rounded-full border-red-300 text-red-600 hover:bg-red-50" onClick={cancelGoods} data-testid="goods-cancel-refund"><Banknote className="mr-2 h-4 w-4" /> Cancel &amp; full refund</Button>
              </div>
            )}
          </div>
        </div>
      )}

      {ride.status === "no_driver" && (
        <div className="absolute inset-x-0 bottom-0 z-[1000] rounded-t-2xl bg-white p-4 shadow-2xl" data-testid="trip-no-driver-panel">
          <div className="mx-auto max-w-2xl space-y-3">
            <div>
              <p className="text-lg font-bold text-ink">We couldn't find a delivery partner yet</p>
              <p className="text-sm text-ink-soft">No partner accepted in the last hour. Keep trying, or cancel for a full refund.</p>
            </div>
            {isGoods && <GoodsInfo ride={ride} />}
            <div className="flex gap-2">
              <Button className="flex-1 rounded-full bg-brand text-white hover:bg-brand-600" onClick={retrySearch} data-testid="goods-retry-search"><Loader2 className="mr-2 h-4 w-4" /> Keep trying</Button>
              <Button variant="outline" className="flex-1 rounded-full border-red-300 text-red-600 hover:bg-red-50" onClick={cancelGoods} data-testid="goods-no-driver-cancel"><Banknote className="mr-2 h-4 w-4" /> Cancel &amp; refund</Button>
            </div>
          </div>
        </div>
      )}

      {isGoods && ["driver_assigned", "driver_arriving", "in_progress"].includes(ride.status) && (
        <div className="absolute inset-x-0 bottom-0 z-[1000] rounded-t-2xl bg-white p-4 shadow-2xl" data-testid="trip-goods-panel">
          <div className="mx-auto max-w-2xl space-y-3">
            <GoodsProgress ride={ride} />
            {ride.start_code && ["driver_assigned", "driver_arriving"].includes(ride.status) && (
              <div className="rounded-xl bg-brand-50 p-3 text-center" data-testid="goods-pickup-otp">
                <p className="text-xs font-semibold text-brand">Pickup code — show this to your delivery partner to confirm pickup</p>
                <p className="mt-0.5 text-3xl font-black tracking-[0.3em] text-brand" data-testid="goods-pickup-otp-value">{ride.start_code}</p>
              </div>
            )}
            <GoodsInfo ride={ride} showFare />
            {["searching", "driver_assigned", "driver_arriving"].includes(ride.status) && (
              <p className="rounded-xl bg-surface-100 px-3 py-2 text-xs text-ink-soft" data-testid="goods-estimate-note">Estimated total {ride.payment_method === "razorpay" ? "(prepaid)" : "collected at pickup"}. Final charges may increase with traffic or time taken — any remaining balance is added to your next ride.</p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" className="rounded-full"><Link to={`/app/chat/${ride.ride_id}`} data-testid="trip-goods-chat"><MessageCircle className="mr-2 h-4 w-4" /> Chat with partner<ChatUnreadBadge chatId={ride.ride_id} /></Link></Button>
              {ride.driver?.phone && <Button asChild variant="outline" className="rounded-full"><a href={`tel:${ride.driver.phone}`}><Phone className="mr-2 h-4 w-4" /> Call</a></Button>}
              {ride.receiver_phone && <Button variant="outline" className="rounded-full" onClick={shareReceiver} disabled={sharing} data-testid="trip-share-receiver">{sharing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Share tracking</Button>}
              {["driver_assigned", "driver_arriving"].includes(ride.status) && (
                <>
                  <Button variant="outline" className="rounded-full" onClick={openGoodsEdit} data-testid="goods-edit-open"><Pencil className="mr-2 h-4 w-4" /> Edit</Button>
                  <Button variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50" onClick={cancelGoodsAssigned} data-testid="goods-cancel"><X className="mr-2 h-4 w-4" /> Cancel delivery</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {ride.status === "payment_pending" && (
        <div className="absolute inset-x-0 bottom-0 z-[1000] rounded-t-2xl bg-white p-4 shadow-2xl" data-testid="trip-payment-panel">
          <div className="mx-auto max-w-2xl space-y-3">
            {isGoods && <GoodsProgress ride={ride} />}
            {ride.fare_reconciled && <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700" data-testid="trip-reconcile-note">{ride.reconcile_note}</p>}
            {ride.payment_status === "paid" ? (
              <div data-testid="trip-payment-paid">
                <p className="flex items-center gap-2 text-lg font-bold text-ink"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> {isGoods ? "Delivery completed" : "Ride ended"}</p>
                <p className="text-sm text-ink-soft" data-testid="trip-paid-receipt">{(ride.payment_method || "cash") === "cash" ? `Paid ${inr(ride.amount_paid || ride.fare)} in cash${isGoods ? " at pickup" : ""} ✓` : `Paid ${inr(ride.amount_paid || ride.fare)} online ✓`}</p>
              </div>
            ) : ride.payment_choice === "cash" ? (
              <div className="space-y-2" data-testid="trip-pay-cash-chosen">
                <p className="text-lg font-bold text-ink">Pay {inr(ride.fare)} in cash</p>
                <p className="text-sm text-ink-soft">Hand the {isGoods ? "delivery fee" : "fare"} to your {partnerWord}. You can switch to online below.</p>
                <Button variant="outline" className="w-full rounded-full" onClick={payOnline} data-testid="trip-pay-online-switch"><Smartphone className="mr-2 h-4 w-4" /> Pay online instead</Button>
              </div>
            ) : (
              <div className="space-y-2" data-testid="trip-pay-choice">
                <p className="text-lg font-bold text-ink">{isGoods ? "Delivery completed" : "Ride ended"} — {isGoods ? "fee" : "fare"} {inr(ride.fare)}</p>
                <p className="text-sm text-ink-soft">{payFailed ? "Your last payment didn't confirm. Retry safely — you won't be charged twice." : "Choose how you'd like to pay."}</p>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 rounded-full" onClick={chooseCash} data-testid="trip-pay-cash"><Banknote className="mr-2 h-4 w-4" /> Pay cash</Button>
                  <Button className="flex-1 rounded-full bg-brand text-white hover:bg-brand-600" onClick={payOnline} data-testid="trip-pay-online"><Smartphone className="mr-2 h-4 w-4" /> {payFailed ? "Retry payment" : "Pay online"}</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) closeEdit(); }}>
        <DialogContent className="z-[1001] max-w-md" data-testid="trip-edit-dialog">
          <DialogHeader><DialogTitle>{edit === "stop" ? "Add a stop" : "Change drop location"}</DialogTitle></DialogHeader>
          <AddressAutocomplete
            value={text}
            onTextChange={setText}
            onSelect={(p) => setPicked(p)}
            placeholder="Search for a place…"
            testId="trip-edit-autocomplete"
          />
          {picked && <p className="text-xs text-ink-soft" data-testid="trip-edit-picked">Selected: {picked.address}</p>}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={closeEdit} data-testid="trip-edit-cancel">Cancel</Button>
            <Button className="rounded-full bg-brand text-white hover:bg-brand-600" disabled={busy || !picked} onClick={submitEdit} data-testid="trip-edit-confirm">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {edit === "stop" ? "Add stop & update fare" : "Update drop & fare"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={goodsEdit} onOpenChange={(o) => { if (!o) { setGoodsEdit(false); setGQuote(null); } }}>
        <DialogContent className="z-[1001] max-w-md" data-testid="goods-edit-dialog">
          <DialogHeader><DialogTitle>{gQuote ? "Confirm fare change" : "Edit delivery"}</DialogTitle></DialogHeader>
          {gQuote ? (
            <div className="space-y-3" data-testid="goods-edit-quote">
              <div className="rounded-xl border border-ink-100 bg-ink-50/60 p-3 text-sm">
                <div className="flex items-center justify-between py-1">
                  <span className="text-ink-soft">Current fare</span>
                  <span className="font-medium text-ink" data-testid="goods-quote-old">{inr(gQuote.old_fare)}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className="text-ink-soft">New fare{gQuote.distance_km ? ` · ${gQuote.distance_km} km` : ""}</span>
                  <span className="font-semibold text-ink" data-testid="goods-quote-new">{inr(gQuote.new_fare)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between border-t border-ink-100 pt-2">
                  <span className="font-medium text-ink">Difference</span>
                  <span className={`font-bold ${gQuote.fare_delta > 0 ? "text-amber-600" : gQuote.fare_delta < 0 ? "text-emerald-600" : "text-ink"}`} data-testid="goods-quote-delta">
                    {gQuote.fare_delta > 0 ? "+" : ""}{inr(Math.abs(gQuote.fare_delta))}
                  </span>
                </div>
              </div>
              {gQuote.edit_message ? <p className="text-xs text-ink-muted" data-testid="goods-quote-message">{gQuote.edit_message}</p> : null}
              <DialogFooter>
                <Button variant="outline" onClick={() => setGQuote(null)} data-testid="goods-quote-back">Back</Button>
                <Button onClick={commitGoodsEdit} disabled={gBusy} className="bg-brand text-white hover:bg-brand-600" data-testid="goods-quote-confirm">{gBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Confirm &amp; save</Button>
              </DialogFooter>
            </div>
          ) : (
          <>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-soft">Receiver name</label>
              <Input value={gForm.receiver_name} onChange={(e) => setGForm((f) => ({ ...f, receiver_name: e.target.value }))} data-testid="goods-edit-name" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-soft">Receiver phone</label>
              <Input value={gForm.receiver_phone} onChange={(e) => setGForm((f) => ({ ...f, receiver_phone: e.target.value }))} data-testid="goods-edit-phone" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-soft">Delivery note (optional)</label>
              <Textarea rows={2} value={gForm.delivery_instructions} onChange={(e) => setGForm((f) => ({ ...f, delivery_instructions: e.target.value }))} data-testid="goods-edit-note" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-soft">Drop-off address</label>
              <PlaceInput label={null} value={gForm.drop} near={ride.pickup_lat ? { lat: ride.pickup_lat, lng: ride.pickup_lng } : null} onChange={(v) => setGForm((f) => ({ ...f, drop: v }))} placeholder="New delivery address" testId="goods-edit-drop" />
              <p className="mt-1 text-xs text-ink-muted">Changing the drop re-quotes the fare — you'll see the difference before confirming.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGoodsEdit(false)}>Cancel</Button>
            <Button onClick={saveGoodsEdit} disabled={gBusy || !gDirty} className="bg-brand text-white hover:bg-brand-600" data-testid="goods-edit-save">{gBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {gDropChanged ? "Review fare" : "Save changes"}</Button>
          </DialogFooter>
          </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
